import React, { useEffect, useMemo, useState } from "react";
import { api, fmt } from "../../../services/erpApi";
import { StatCard, PageHeader, LoadingScreen, Modal, EmptyState } from "../../../components/admin/EssUi";
import { useToast } from "../../../components/admin/ToastContext";
import { Plus, RefreshCw, CreditCard, BookOpen, ScrollText, CheckCircle2, Banknote } from "lucide-react";

const typeColor = (t) => {
  const map = {
    sale: { text: "#225f2b", bg: "#f0f7f0" },
    purchase: { text: "#0f766e", bg: "#eef6f3" },
    bank: { text: "#b45309", bg: "#fffbeb" },
    general: { text: "#475569", bg: "#f1f5f9" },
  };
  return map[t] || { text: "#475569", bg: "#f1f5f9" };
};
const typeLabel = (t) => {
  const map = { sale: "Sales", purchase: "Purchases", bank: "Bank", general: "General" };
  return map[t] || t || "—";
};

const moveBadge = (posted, state) => {
  if (posted || state === "posted") return { text: "#225f2b", bg: "#f0f7f0", label: "Posted" };
  if (state === "cancel" || state === "cancelled") return { text: "#be123c", bg: "#fff1f2", label: "Cancelled" };
  return { text: "#b45309", bg: "#fffbeb", label: (state || "draft").toUpperCase() };
};

const AdminPanelJournalPage = () => {
  const toast = useToast();
  const [journals, setJournals] = useState([]);
  const [moves, setMoves] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState(null);
  const [posting, setPosting] = useState(null);

  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", type: "general", is_active: true });

  const load = async () => {
    try {
      const j = await api.journals.list({ limit: 500 });
      setJournals(j || []);
      const m = await api.accounting.moves({ limit: 500 }).catch(() => []);
      setMoves(m || []);
    } catch {
      setJournals([]);
      setMoves([]);
    }
  };

  useEffect(() => {
    (async () => { await load(); setLoaded(true); })();
  }, []);

  useEffect(() => {
    if (selected && !journals.some((j) => j.id === selected.id)) setSelected(null);
  }, [journals, selected]);

  const selectedMoves = useMemo(
    () => moves.filter((m) => m.journal_id === selected?.id || m.journal_name === selected?.name),
    [moves, selected]
  );

  const stats = useMemo(() => {
    const totalMoves = moves.length;
    const postedMoves = moves.filter((m) => m.posted || m.state === "posted").length;
    const pendingMoves = totalMoves - postedMoves;
    const totalDebit = moves.reduce((s, m) => s + (Number(m.total_debit) || 0), 0);
    return { count: journals.length, totalMoves, postedMoves, pendingMoves, totalDebit };
  }, [journals, moves]);

  const handlePost = async (move) => {
    setPosting(move.id);
    try {
      await api.accounting.post(move.id);
      toast(`#${move.name || move.id} posted`, "success");
      await load();
    } catch (e) {
      toast(e.message || "Posting failed", "error");
    }
    setPosting(null);
  };

  const handleCreate = async () => {
    if (!form.code) { toast("Journal code is required", "error"); return; }
    if (!form.name) { toast("Journal name is required", "error"); return; }
    setCreating(true);
    try {
      await api.journals.create({ code: form.code.trim(), name: form.name.trim(), type: form.type, is_active: form.is_active });
      toast("Journal created", "success");
      setShowCreate(false);
      setForm({ code: "", name: "", type: "general", is_active: true });
      await load();
    } catch (e) {
      toast(e.message || "Creation failed", "error");
    }
    setCreating(false);
  };

  const inputStyle = {
    width: "100%", padding: "9px 12px", border: "1px solid #cbd5e1", borderRadius: "6px",
    fontSize: "13.5px", outline: "none", background: "#fff", boxSizing: "border-box",
  };
  const labelStyle = { fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "6px", display: "block" };
  const FormField = ({ label, children }) => (
    <div style={{ marginBottom: "16px" }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );

  if (!loaded) return <LoadingScreen />;

  return (
    <div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: "0 16px" }}>
      <PageHeader
        title="Journals"
        subtitle="Accounting journals and their posted / draft entries"
        actions={
          <button onClick={() => { setForm({ code: "", name: "", type: "general", is_active: true }); setShowCreate(true); }} className="ess-btn ess-btn-primary">
            <Plus size={16} />
            New Journal
          </button>
        }
      />

      <div className="ess-sg ess-sg4" style={{ marginBottom: "24px" }}>
        <StatCard label="Journals" value={String(stats.count)} icon={BookOpen} trend="Available journals" />
        <StatCard label="Entries" value={String(stats.totalMoves)} icon={ScrollText} trend="Journal entries" />
        <StatCard label="Posted" value={String(stats.postedMoves)} icon={CheckCircle2} trend={`${stats.pendingMoves} pending`} />
        <StatCard label="Total Debit" value={fmt.moneyShort(stats.totalDebit)} icon={Banknote} trend={fmt.money(stats.totalDebit)} />
      </div>

      {journals.length === 0 ? (
        <EmptyState icon="📜" title="No journals" subtitle="Journals will appear once configured on the backend" />
      ) : (
        <div style={{ display: "grid", gap: "16px", alignItems: "start" }} className="ess-rg-side">
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {journals.map((j) => {
              const c = typeColor(j.type);
              const isSel = selected?.id === j.id;
              return (
                <button key={j.id} onClick={() => setSelected(j)} style={{ textAlign: "left", padding: "14px 16px", borderRadius: "8px", border: isSel ? "1px solid #225f2b" : "1px solid #e2e7e2", background: isSel ? "#f0f7f0" : "white", cursor: "pointer", transition: "all 0.2s" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                    <CreditCard size={16} style={{ color: "#225f2b", flexShrink: 0 }} />
                    <span style={{ fontSize: "14px", fontWeight: "600", color: "#1e293b" }}>{j.name || "—"}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontFamily: "monospace", fontSize: "12px", color: "#64748b" }}>{j.code || ""}</span>
                    <span style={{ background: c.bg, color: c.text, padding: "2px 10px", borderRadius: "6px", fontSize: "11px", fontWeight: "500" }}>{typeLabel(j.type)}</span>
                  </div>
                  <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "6px" }}>{j.move_count ?? 0} entries</div>
                </button>
              );
            })}
          </div>

          <div style={{ background: "white", borderRadius: "8px", border: "1px solid #e2e7e2", overflow: "hidden" }}>
            {!selected ? (
              <EmptyState icon="📜" title="Select a journal" subtitle="Pick a journal on the left to view its entries" />
            ) : selectedMoves.length === 0 ? (
              <EmptyState icon="📭" title="No entries" subtitle="This journal has no entries yet" />
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                  <thead>
                    <tr style={{ background: "#fafbfa", borderBottom: "1px solid #e2e7e2" }}>
                      <th className="ess-th" style={{ minWidth: "140px" }}>Number</th>
                      <th className="ess-th" style={{ minWidth: "100px" }}>Date</th>
                      <th className="ess-th" style={{ minWidth: "90px" }}>State</th>
                      <th className="ess-th ess-th-r" style={{ minWidth: "110px" }}>Debit</th>
                      <th className="ess-th ess-th-r" style={{ minWidth: "110px" }}>Credit</th>
                      <th className="ess-th" style={{ textAlign: "center", minWidth: "90px" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedMoves.map((m, index) => {
                      const b = moveBadge(m, m.state);
                      const isPosted = m.posted || m.state === "posted";
                      return (
                        <tr key={m.id} className="ess-tr" style={{ borderBottom: index < selectedMoves.length - 1 ? "1px solid #f1f3f0" : "none" }}>
                          <td className="ess-td" style={{ fontWeight: "600", color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.name || `MOVE ${m.id}`}</td>
                          <td className="ess-td" style={{ color: "#64748b", whiteSpace: "nowrap" }}>{fmt.date(m.date)}</td>
                          <td className="ess-td" style={{ whiteSpace: "nowrap" }}>
                            <span style={{ background: b.bg, color: b.text, padding: "3px 10px", borderRadius: "6px", fontSize: "12px", fontWeight: "500" }}>{b.label}</span>
                          </td>
                          <td className="ess-td" style={{ color: "#475569", textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(m.total_debit)}</td>
                          <td className="ess-td" style={{ color: "#475569", textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(m.total_credit)}</td>
                          <td className="ess-td" style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                            {isPosted ? (
                              <span style={{ fontSize: "12px", color: "#94a3b8" }}>—</span>
                            ) : (
                              <button onClick={() => handlePost(m)} disabled={posting === m.id} className="ess-btn ess-btn-primary" style={{ padding: "6px 14px", fontSize: "12px" }}>
                                {posting === m.id ? <RefreshCw size={12} className="animate-spin" /> : null}
                                {posting === m.id ? "Posting…" : "Post"}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="New Journal" size="md">
        <div>
          <FormField label="Code *">
            <input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} placeholder="GENE" style={inputStyle} />
          </FormField>
          <FormField label="Name *">
            <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Journal Général" style={inputStyle} />
          </FormField>
          <FormField label="Type">
            <select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} style={inputStyle}>
              <option value="general">General</option>
              <option value="sale">Sales</option>
              <option value="purchase">Purchases</option>
              <option value="bank">Bank</option>
            </select>
          </FormField>
          <FormField label="Active">
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />
              <span style={{ fontSize: "13px", color: "#475569" }}>Journal is active</span>
            </label>
          </FormField>
          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", borderTop: "1px solid #e2e7e2", paddingTop: "20px" }}>
            <button onClick={() => setShowCreate(false)} className="ess-btn ess-btn-secondary">Cancel</button>
            <button onClick={handleCreate} disabled={creating} className="ess-btn ess-btn-primary">
              {creating ? "Creating…" : "Create Journal"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminPanelJournalPage;